import React, { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'

import type {
  OperationMapPoint,
  OperationResult,
} from '../operations'
import { ensureBoundsVisible, ensurePointVisible } from '../mapCamera'
import {
  beginCameraIntent,
  clearCameraIntent,
  softFollowPoint,
  noteUserCameraGesture,
  ENTITY_POINT_ZOOM,
  type CameraFocusAnchor,
} from '../mapCamera'

import type { MappableEntity, MapStyleId } from './controlCenterMapTypes'
import { MAP_STYLES } from './mapStyles'

import type { TigerwebFeatureCollection } from '../../../services/tigerwebBoundaryService'

export function FocusOperationResultPoint({
  point,
  focusToken,
  focusAnchor,
  layoutFocusToken = 0,
}: {
  point: OperationMapPoint | null
  focusToken: number
  focusAnchor?: CameraFocusAnchor
  /** Bump when the mobile output panel opens so the pin re-frames into the top half. Closing must not bump this. */
  layoutFocusToken?: number
}) {
  const map = useMap()
  const focusAnchorRef = useRef(focusAnchor)
  focusAnchorRef.current = focusAnchor
  useEffect(() => {
    if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return
    if (focusToken <= 0) return
    ensurePointVisible(map, point.lat, point.lng, {
      preferredZoom: ENTITY_POINT_ZOOM,
      forceCenter: true,
      focusAnchor: focusAnchorRef.current,
    })
  }, [point?.id, point?.lat, point?.lng, focusToken, layoutFocusToken, map])
  return null
}

export function ControlCenterMapBasemapLayer({ mapStyleId }: { mapStyleId: MapStyleId }) {
  const map = useMap()
  useEffect(() => {
    const { url, attribution, maxZoom } = MAP_STYLES[mapStyleId]
    const layer = L.tileLayer(url, {
      attribution,
      ...(maxZoom != null ? { maxZoom } : {}),
    })
    layer.addTo(map)
    layer.bringToBack()
    return () => {
      map.removeLayer(layer)
    }
  }, [map, mapStyleId])
  return null
}

export function TrackUserCameraGestures() {
  const map = useMap()
  useEffect(() => {
    const onUser = () => noteUserCameraGesture()
    map.on('dragstart', onUser)
    map.on('zoomstart', onUser)
    return () => {
      map.off('dragstart', onUser)
      map.off('zoomstart', onUser)
    }
  }, [map])
  return null
}

/** Keep Leaflet projections correct after flex/layout settles. */
export function InvalidateMapSize() {
  const map = useMap()
  useEffect(() => {
    const run = () => map.invalidateSize({ animate: false })
    run()
    const t1 = window.setTimeout(run, 100)
    const t2 = window.setTimeout(run, 400)
    window.addEventListener('resize', run)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.removeEventListener('resize', run)
    }
  }, [map])
  return null
}

export function FlyToSelectedEntity({
  entity,
  enabled,
  focusToken = 0,
  focusAnchor,
  layoutFocusToken = 0,
}: {
  entity: MappableEntity | null
  enabled: boolean
  /** Bumped on explicit focus (e.g. omni badge click) so the same entity re-frames. */
  focusToken?: number
  focusAnchor?: CameraFocusAnchor
  /** Bump when the mobile output panel opens so the pin re-frames into the top half. Closing must not bump this. */
  layoutFocusToken?: number
}) {
  const map = useMap()
  const framedForKeyRef = useRef<string | null>(null)
  const prevPosRef = useRef<{ id: string; lat: number; lng: number } | null>(null)
  const focusAnchorRef = useRef(focusAnchor)
  focusAnchorRef.current = focusAnchor

  // Frame when selection / focus token / panel-open layout changes. Closing the
  // output panel must not re-frame — operations already placed the camera.
  // Live lat/lng updates are handled by soft-follow below.
  useEffect(() => {
    if (!enabled) {
      framedForKeyRef.current = null
      return
    }
    if (!entity) {
      framedForKeyRef.current = null
      return
    }
    if (!Number.isFinite(entity.lat) || !Number.isFinite(entity.lng)) return

    const intentKey = `entity:${entity.id}:${focusToken}:${layoutFocusToken}`
    if (framedForKeyRef.current === intentKey) return

    beginCameraIntent(intentKey)
    framedForKeyRef.current = intentKey
    prevPosRef.current = { id: entity.id, lat: entity.lat, lng: entity.lng }
    ensurePointVisible(map, entity.lat, entity.lng, {
      preferredZoom: ENTITY_POINT_ZOOM,
      forceCenter: true,
      focusAnchor: focusAnchorRef.current,
    })
  }, [enabled, entity?.id, entity?.lat, entity?.lng, focusToken, layoutFocusToken, map])

  // Soft-follow moving pins only after the initial frame, and only while the
  // user hasn't taken over the camera.
  useEffect(() => {
    if (!enabled || !entity) {
      if (!entity) prevPosRef.current = null
      return
    }
    if (!framedForKeyRef.current?.startsWith(`entity:${entity.id}:`)) return
    if (!Number.isFinite(entity.lat) || !Number.isFinite(entity.lng)) return

    const prev = prevPosRef.current
    if (!prev || prev.id !== entity.id) {
      prevPosRef.current = { id: entity.id, lat: entity.lat, lng: entity.lng }
      return
    }
    if (prev.lat === entity.lat && prev.lng === entity.lng) return
    prevPosRef.current = { id: entity.id, lat: entity.lat, lng: entity.lng }
    softFollowPoint(map, entity.lat, entity.lng, { focusAnchor: focusAnchorRef.current })
  }, [enabled, entity?.id, entity?.lat, entity?.lng, focusToken, map])

  return null
}

export function MunicipalityBoundaryLayer({
  geojson,
  color,
  fitKey,
  maxZoom,
  weight = 2.5,
  fillOpacity = 0.18,
}: {
  geojson: TigerwebFeatureCollection | null
  color: string
  fitKey: string | null
  maxZoom: number
  weight?: number
  fillOpacity?: number
}) {
  const map = useMap()

  useEffect(() => {
    if (!geojson || geojson.features.length === 0) return

    const layer = L.geoJSON(geojson as GeoJSON.GeoJsonObject, {
      style: {
        color,
        weight,
        opacity: 0.95,
        fillColor: color,
        fillOpacity,
      },
      interactive: false,
    })
    layer.addTo(map)

    return () => {
      map.removeLayer(layer)
    }
  }, [geojson, color, weight, fillOpacity, map])

  useEffect(() => {
    if (!geojson || !fitKey || geojson.features.length === 0) return

    const intentKey = `boundary:${fitKey}`
    beginCameraIntent(intentKey)

    const bounds = L.geoJSON(geojson as GeoJSON.GeoJsonObject).getBounds()
    ensureBoundsVisible(map, bounds, {
      padding: [48, 48],
      maxZoom,
    })
  }, [fitKey, maxZoom, map, geojson])

  return null
}

export function StopMapOnSelectionChange({ selectedId }: { selectedId: string | null }) {
  const map = useMap()
  useEffect(() => {
    map.stop()
    // Always clear so the next entity/boundary frame is allowed (dedupe keys
    // were leaving camera intents sticky across municipality ↔ pin switches).
    clearCameraIntent()
  }, [selectedId, map])
  return null
}

export function FitOperationResultBounds({
  result,
  origin,
  nearbyRadiusMiles = null,
}: {
  result: OperationResult | null
  origin: { lat: number; lng: number } | null
  nearbyRadiusMiles?: number | null
}) {
  const map = useMap()
  const framedOpRef = useRef<string | null>(null)

  useEffect(() => {
    if (!result) {
      framedOpRef.current = null
      return
    }
    // Stay on the selected entity — jurisdiction pins can be statewide and would yank the camera.
    if (result.operatorId === 'local-license-check') {
      return
    }

    const intentKey = `operation:${result.operatorId}:${result.ranAt}`
    beginCameraIntent(intentKey)
    framedOpRef.current = intentKey

    const pts: Array<[number, number]> = []
    if (origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lng)) {
      pts.push([origin.lat, origin.lng])
    }
    for (const p of result.mapPoints) {
      if (Number.isFinite(p.lat) && Number.isFinite(p.lng)) pts.push([p.lat, p.lng])
    }
    if (result.routeLine) {
      for (const p of result.routeLine) {
        if (Number.isFinite(p[0]) && Number.isFinite(p[1])) pts.push(p)
      }
    }

    let bounds: L.LatLngBounds | null = pts.length > 0 ? L.latLngBounds(pts) : null
    if (
      result.operatorId === 'nearby' &&
      origin &&
      Number.isFinite(origin.lat) &&
      Number.isFinite(origin.lng) &&
      nearbyRadiusMiles != null &&
      nearbyRadiusMiles > 0
    ) {
      // Don't call L.circle(...).getBounds() — Circle needs to be on a map first.
      const radiusMeters = nearbyRadiusMiles * 1609.344
      const latDelta = radiusMeters / 111_320
      const cosLat = Math.cos((origin.lat * Math.PI) / 180)
      const lngDelta = cosLat > 1e-6 ? radiusMeters / (111_320 * cosLat) : 180
      const circleBounds = L.latLngBounds(
        [origin.lat - latDelta, origin.lng - lngDelta],
        [origin.lat + latDelta, origin.lng + lngDelta],
      )
      bounds = bounds ? bounds.extend(circleBounds) : circleBounds
    }

    if (!bounds || !bounds.isValid()) return

    if (pts.length === 1 && !(result.operatorId === 'nearby' && nearbyRadiusMiles)) {
      ensurePointVisible(map, pts[0][0], pts[0][1], {
        preferredZoom: ENTITY_POINT_ZOOM,
      })
    } else {
      ensureBoundsVisible(map, bounds, {
        padding: [56, 56],
        maxZoom: 12,
      })
    }

    return () => {
      if (framedOpRef.current === intentKey) framedOpRef.current = null
    }
  }, [
    result?.ranAt,
    result?.operatorId,
    origin?.lat,
    origin?.lng,
    nearbyRadiusMiles,
    map,
  ])

  return null
}

